import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Languages } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { 
  changeLanguage, 
  getCurrentLanguage, 
  getCurrentDirection,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage 
} from '@/i18n/config'
import clsx from 'clsx'

type LanguageSwitcherProps = {
  onOpenChange?: (open: boolean) => void
  /**
   * Icon classes. Defaults to a fixed square size (`size-8`) so the SVG
   * height is pinned in `rem` and does not scale with browser zoom — the
   * previous `w-8` set width only, leaving height at `1em` (font-size
   * relative), which caused the zoom-resize + off-center bug (ENG-2482).
   */
  iconClassName?: string
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  onOpenChange,
  iconClassName = 'size-8 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer',
}) => {
  const { t } = useTranslation()
  const [currentLang, setCurrentLang] = useState<SupportedLanguage>(getCurrentLanguage())
  const isRTL = getCurrentDirection() === 'rtl'

  const handleLanguageChange = async (lang: SupportedLanguage) => {
    try {
      await changeLanguage(lang)
      setCurrentLang(lang)
    } catch (error) {
      console.error('Failed to change language:', error)
    }
  }

  const getLanguageDisplayName = (languageCode: string): string => {
    const languageMap: Record<string, string> = {
      en: t('nav:language_switcher.languages.en'),
      es: t('nav:language_switcher.languages.es'),
      ja: t('nav:language_switcher.languages.ja'),
      pt: t('nav:language_switcher.languages.pt'),
      ar: t('nav:language_switcher.languages.ar'),
    }
    return languageMap[languageCode] || languageCode
  }

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Languages className={iconClassName} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align={isRTL ? "end" : "center"} >
        {Object.entries(SUPPORTED_LANGUAGES).map(([code]) => (
        <React.Fragment key={code}>
          <DropdownMenuItem
          className={clsx(
            currentLang === code ? 'bg-gray-100 dark:bg-dark-accent' : '',
            isRTL ? 'text-right' : 'text-left'
          )}
              onClick={() => handleLanguageChange(code as SupportedLanguage)}
          >
              <span className={clsx(isRTL ? 'text-right w-full' : 'text-left')}>{getLanguageDisplayName(code)}</span>
              
          </DropdownMenuItem>
        </React.Fragment>

        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default LanguageSwitcher
